import {createAction, createGetter, createMutation, CustomStore, mutate, on, select} from '@softline/core';
import {Absatzplan, PatchAbsatzplan} from '../types/absatzplan';
import {lastValueFrom} from 'rxjs';
import {AbsatzplanService} from '../services/absatzplan.service';
import {AbsatzplanStatus} from '../types/absatzplan-status';

export interface AbsatzplanState {
  loading: boolean;
  data: Absatzplan | null
}

const mutations = {
  set: createMutation<AbsatzplanState, AbsatzplanState>('produktion/absatzplan/AbsatzplanStore/mutations/set'),
  patch: createMutation<AbsatzplanState, Partial<AbsatzplanState>>('produktion/absatzplan/AbsatzplanStore/mutations/patch'),
  resetToPlangruppen: createMutation<AbsatzplanState, void>('produktion/absatzplan/AbsatzplanStore/mutations/resetToPlangruppen'),
};

const getters = {
  loading: createGetter<AbsatzplanState, boolean>('produktion/absatzplan/AbsatzplanStore/getters/loading'),
  data: createGetter<AbsatzplanState, Absatzplan>('produktion/absatzplan/AbsatzplanStore/getters/data')
};

const actions = {
  load: createAction<AbsatzplanState, { queryParams: Record<string, unknown>; formData: Record<string, unknown> }, Absatzplan>('produktion/absatzplan/AbsatzplanStore/actions/load'),
  patch: createAction<AbsatzplanState, { id: number; queryParams: Record<string, unknown>; body: PatchAbsatzplan }, Absatzplan>('produktion/absatzplan/AbsatzplanStore/actions/patch'),
  copy: createAction<AbsatzplanState, { idSource: number; targetName: string; datum: string }, Absatzplan>('produktion/absatzplan/AbsatzplanStore/actions/copy'),
  saveComment: createAction<AbsatzplanState, { queryParams: Record<string, unknown>, kommentar: string | null }, Absatzplan>('produktion/absatzplan/AbsatzplanStore/actions/saveComment'),
  nextStatus: createAction<AbsatzplanState, { queryParams: Record<string, unknown>, nextStatus: AbsatzplanStatus }, Absatzplan>('produktion/absatzplan/AbsatzplanStore/actions/nextStatus'),
  vorhandenenKopfEinlesen: createAction<AbsatzplanState, { queryParams: Record<string, unknown> }, Absatzplan>('produktion/absatzplan/AbsatzplanStore/actions/vorhandenenKopfEinlesen'),
  deleteVorhandenenKopf: createAction<AbsatzplanState, void, boolean>('produktion/absatzplan/AbsatzplanStore/actions/deleteVorhandenenKopf'),
  delete: createAction<AbsatzplanState,  number, Absatzplan>('produktion/absatzplan/AbsatzplanStore/actions/delete'),
  save: createAction<AbsatzplanState, { planvariante: string[] }, Absatzplan>('produktion/absatzplan/AbsatzplanStore/actions/save'),
}

export const AbsatzplanStore: CustomStore<AbsatzplanState, typeof mutations, typeof getters, typeof actions> = {
  actions,
  mutations,
  getters,
  feature: {
    initialState: {
      loading: false,
      data: null
    },
    getters: [
      select(getters.loading, ({ state }) => state.loading),
      select(getters.data, ({ state }) => state.data),
    ],
    mutations: [
      mutate(mutations.set, ({ params }) => ({ ...params })),
      mutate(mutations.patch, ({ state, params }) => ({ ...state, ...params })),
      mutate(mutations.resetToPlangruppen, ({ state }) => ({
        ...state,
        data: state?.data ? {
          ...state.data,
          plaene: state.data.plaene.filter(o => o.typ === 'PG') ?? []
        } : null
      })),
    ],
    actions: [
      on(actions.load, async ({ params, injector, commit, featureName }) => {
        commit(featureName, mutations.patch, { loading: true });
        const service = injector.get(AbsatzplanService);
        const result = await lastValueFrom(service.load(params));
        commit(featureName, mutations.set, { loading: false, data: result });
        return result;
      }),
      on(actions.patch, async ({ params, injector, get, commit, featureName}) => {
        const service = injector.get(AbsatzplanService);
        const { id, ...rest} = params;
        const result = await lastValueFrom(service.patch(id, rest as never));

        const data = get(featureName, getters.data)
        const storePlane = [...data.plaene];
        const plaeneEntities = {}

        storePlane.forEach((plan, index) => {
          plaeneEntities[plan.id] = { plan, index }
        });

        for (const plan of result.plaene) {
          const existing = plaeneEntities[plan.id];

          if (!existing) {
            continue;
          }

          const existingContent = storePlane[existing.index];
          storePlane[existing.index] = {
            ...existingContent,
            daten: plan.daten
          }
        }

        commit(featureName, mutations.patch, {
          data: {
            ...get(featureName, getters.data),
            plaene: storePlane
          }
        });

        return result;
      }),
      on(actions.saveComment, async ({ params, injector, state, commit, featureName}) => {
        const service = injector.get(AbsatzplanService);

        if (!state?.data?.id)
          return state?.data;

        const result = await lastValueFrom(
          service.updateKommentar(
            state.data!.id,
            params
          )
        );

        commit(featureName, mutations.patch, {
          data: {
            ...state.data!,
            kommentar: result?.kommentar ?? null
          }
        });

        return state.data;
      }),
      on(actions.nextStatus, async ({ params, injector, state, get, commit, featureName}) => {
        const service = injector.get(AbsatzplanService);

        const result = await lastValueFrom(
          service.freigeben(
            state.data!.id,
            { queryParams: params.queryParams, status: params.nextStatus }
          )
        );

        commit(featureName, mutations.patch, {
          data: {
            ...state.data!,
            status: result.status
          }
        });

        return get(featureName, getters.data);
      }),
      on(actions.save, async ({ injector, state, commit, params, featureName}) => {
        const service = injector.get(AbsatzplanService);

        const result = await lastValueFrom(service.speichern({ id: state.data!.id, planvariante: params.planvariante }));

        commit(featureName, mutations.patch, {
          data: {
            ...state.data!,
            status: result.status
          }
        });

        return state.data;
      }),
      on(actions.copy, async ({ injector, state, params}) => {
        const service = injector.get(AbsatzplanService);
        const result = await lastValueFrom(service.kopieren(params));
        return state.data;
      }),
      on(actions.delete, async ({ injector, state, params}) => {
        const service = injector.get(AbsatzplanService);
        const result = await lastValueFrom(service.loeschen(params));
        return state.data;
      }),
      on(actions.deleteVorhandenenKopf, async ({ injector, state, params}) => {
        const service = injector.get(AbsatzplanService);
        const result = await lastValueFrom(service.vorhandenenKopfLoeschen());
        return result;
      }),
      on(actions.vorhandenenKopfEinlesen, async ({ injector, params, commit, featureName }) => {
        commit(featureName, mutations.patch, { loading: true });
        const service = injector.get(AbsatzplanService);
        const result = await lastValueFrom(service.vorhandenenKopfEinlesen(params?.queryParams ?? {}));
        commit(featureName, mutations.set, { loading: false, data: result });
        return result;
      })
    ],
  }
}
